import { PDFDocument } from 'pdf-lib';

export const getCSRFToken = (): string => {
  const cookieValue = document.cookie
    .split('; ')
    .find((row) => row.startsWith('csrftoken='))
    ?.split('=')[1];
  return cookieValue || '';
};

/**
 * Creates a deep copy of FormData with a cleaned PDF (metadata and XML removed)
 * @param data Original FormData containing a PDF file
 * @param key The key in FormData where the PDF file is stored
 * @returns A new FormData object with the cleaned PDF, original FormData remains unchanged
 */
export const cleanPDF = async (
  data: FormData,
  key: string
): Promise<FormData> => {
  // Get the file from FormData
  const originalFile = data.get(key) as File;
  if (
    !originalFile ||
    originalFile.type !== 'application/pdf' ||
    !originalFile.name.toLowerCase().endsWith('.pdf')
  ) {
    return data;
  }

  // Create a deep copy of the FormData
  const newFormData = new FormData();

  // Copy all other entries from the original FormData
  for (const [entryKey, entryValue] of data.entries()) {
    if (entryKey !== key) {
      newFormData.append(entryKey, entryValue);
    }
  }

  // Process the PDF file
  const arrayBuffer = await originalFile.arrayBuffer();

  // Load the original PDF
  const originalPdf = await PDFDocument.load(arrayBuffer);

  // Create a new PDF
  const newPdf = await PDFDocument.create();

  // Copy all pages from the original to the new PDF
  const pages = await newPdf.copyPages(
    originalPdf,
    originalPdf.getPageIndices()
  );
  pages.forEach((page) => newPdf.addPage(page));

  // Save new PDF (no metadata, no XMP, no extras)
  const cleanedBytes = await newPdf.save();

  // Create a new file with the cleaned PDF data
  const cleanedBlob = new Blob([cleanedBytes], { type: 'application/pdf' });
  const cleanedFile = new File([cleanedBlob], originalFile.name, {
    type: originalFile.type,
    lastModified: originalFile.lastModified,
  });

  // Add the cleaned file to the new FormData
  newFormData.append(key, cleanedFile);

  return newFormData;
};

export const formatCurrency = (amount: number) =>
  Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
